import { capitalize } from 'lodash'
import { XIcon } from 'lucide-react'
import { useState } from 'react'
import { useNavigate } from 'react-router-dom'

import { paths } from 'src/config/paths'
import DotList from 'src/shared/components/ui/dot-list'
import ImageCarousel from 'src/shared/components/ui/miscellaneous/carousel/image-carousel'
import { Dialog, DialogContent } from 'src/shared/components/ui/popups/dialog'
import useBreakpoint from 'src/shared/hooks/use-breakpoint'
import { cn } from 'src/shared/utils/cn'
import { formatCurrency } from 'src/shared/utils/format-currency'

import type { PortfolioImage, PortfolioItem } from '../../../schemas/portfolio'
import { CoverPhotoFallback } from '../../profile-photos/cover-photo'
import PublicUserProfileFooter from '../ui/footer'

interface PortfolioDialogProps {
  isOpen: boolean
  setOpen: (open: boolean) => void
  item: PortfolioItem
  userId: number
  username: string
}

function SingleImage({ image, imageOnClick }: { image: PortfolioImage; imageOnClick?: () => void }) {
  return (
    <div
      className={cn('flex h-full w-full overflow-hidden rounded-[6px]', imageOnClick && 'hover:cursor-pointer')}
      onClick={imageOnClick}
    >
      <img src={image.photoUrl || ''} alt="Portfolio" className="size-full object-cover" />
    </div>
  )
}

function ImageGrid({ images, imageOnClick }: { images: PortfolioImage[]; imageOnClick: (index: number) => void }) {
  if (images.length === 0) {
    return (
      <div className="flex h-full flex-col gap-[10px]">
        <img src={CoverPhotoFallback} alt="Portfolio" className="size-full object-cover" />
      </div>
    )
  }

  if (images.length === 1) {
    return <SingleImage image={images[0]} imageOnClick={() => imageOnClick(0)} />
  }

  if (images.length === 2) {
    return (
      <div className="flex h-full flex-col gap-[10px]">
        <SingleImage image={images[0]} imageOnClick={() => imageOnClick(0)} />
        <SingleImage image={images[1]} imageOnClick={() => imageOnClick(1)} />
      </div>
    )
  }

  if (images.length === 3) {
    return (
      <div className="flex h-full flex-col gap-[10px]">
        <SingleImage image={images[0]} imageOnClick={() => imageOnClick(0)} />
        <div className="flex flex-row gap-[10px]">
          <SingleImage image={images[1]} imageOnClick={() => imageOnClick(1)} />
          <SingleImage image={images[2]} imageOnClick={() => imageOnClick(2)} />
        </div>
      </div>
    )
  }

  if (images.length === 4) {
    return (
      <div className="flex h-full flex-col gap-[10px]">
        <SingleImage image={images[0]} imageOnClick={() => imageOnClick(0)} />
        <SingleImage image={images[1]} imageOnClick={() => imageOnClick(1)} />
        <div className="flex flex-row gap-[10px]">
          <SingleImage image={images[2]} imageOnClick={() => imageOnClick(2)} />
          <SingleImage image={images[3]} imageOnClick={() => imageOnClick(3)} />
        </div>
      </div>
    )
  }

  // 5 or more images
  return (
    <div className="flex h-full flex-col gap-[10px]">
      <div className="flex h-1/2">
        <SingleImage image={images[0]} imageOnClick={() => imageOnClick(0)} />
      </div>
      <div className="flex h-1/2 flex-col gap-[10px]">
        <div className="flex h-1/2 flex-row gap-[10px]">
          <SingleImage image={images[1]} imageOnClick={() => imageOnClick(1)} />
          <SingleImage image={images[2]} imageOnClick={() => imageOnClick(2)} />
        </div>
        <div className="flex h-1/2 flex-row gap-[10px]">
          <SingleImage image={images[3]} imageOnClick={() => imageOnClick(3)} />
          <SingleImage image={images[4]} imageOnClick={() => imageOnClick(4)} />
        </div>
      </div>
    </div>
  )
}

function CloseButton({
  onClick,
  className,
  xIconSize,
}: {
  onClick: () => void
  className?: string
  xIconSize?: number
}) {
  return (
    <div
      onClick={onClick}
      className={cn(
        'flex size-[50px] items-center justify-center rounded-full bg-[#F8F7F5] hover:cursor-pointer',
        className,
      )}
    >
      <XIcon size={xIconSize || 28} className="text-primary" />
    </div>
  )
}

export function ImageCarouselDialog({
  isOpen,
  setOpen,
  images,
  startIndex,
}: {
  isOpen: boolean
  setOpen: (open: boolean) => void
  images: PortfolioImage[]
  startIndex?: number
}) {
  return (
    <Dialog open={isOpen} onOpenChange={setOpen}>
      <DialogContent
        hideClose
        className="w-full max-w-[1000px] overflow-hidden rounded-[20px] border-none bg-transparent p-0 sm:rounded-[40px] md:p-0"
      >
        <div className="absolute right-[35px] top-[37px] z-10">
          <CloseButton onClick={() => setOpen(false)} />
        </div>
        <ImageCarousel
          images={images.map((image) => image.photoUrl)}
          imageContainerClassName="h-[calc(100vh-100px)]"
          showSideArrows={true}
          loop={false}
          startIndex={startIndex}
        />
      </DialogContent>
    </Dialog>
  )
}

export default function PortfolioDialog({ isOpen, setOpen, item, userId, username }: PortfolioDialogProps) {
  const { isSmTabletOrSmaller } = useBreakpoint()
  const listingStatus = item.listingStatus ? capitalize(item.listingStatus + ' listing') : null
  const [imageCarouselState, setImageCarouselState] = useState({
    isOpen: false,
    startIndex: 0,
  })

  const navigate = useNavigate()

  const imageOnClick = (index: number) => {
    setImageCarouselState({
      isOpen: true,
      startIndex: index,
    })
  }

  const onSendInquiry = () => {
    navigate(paths.marketplace.instantMatch.userId.getHref(userId.toString()))
  }

  const hasImages = item.images.length > 0

  if (isSmTabletOrSmaller) {
    return (
      <Dialog open={isOpen} onOpenChange={setOpen}>
        <DialogContent hideClose className="size-full max-h-full max-w-full rounded-none border-none">
          <div
            className="overflow-y-auto 
              [&::-webkit-scrollbar-thumb]:rounded-full
            [&::-webkit-scrollbar-thumb]:bg-[#606F6A]
              [&::-webkit-scrollbar-track]:my-4
              [&::-webkit-scrollbar-track]:rounded-full
            [&::-webkit-scrollbar-track]:bg-[#D8D8D6]
              [&::-webkit-scrollbar]:w-[3px]"
          >
            <div className="absolute right-[20px] top-[35px] z-10">
              <CloseButton className="size-[40px]" onClick={() => setOpen(false)} xIconSize={24} />
            </div>
            {hasImages && (
              <ImageCarousel
                images={item.images.map((image) => image.photoUrl)}
                imageContainerClassName="h-[303px]"
                className="absolute inset-x-0 top-0 rounded-none"
                showSideArrows={false}
                loop={false}
                startIndex={imageCarouselState.startIndex}
                dotContainerClassName="pb-[35px]"
              />
            )}
            <div
              className={cn(
                'pointer-events-none mt-[303px] flex translate-y-[-20px] flex-col items-center rounded-t-[20px] bg-white px-5 pb-[80px]',
                !hasImages && 'mt-20',
              )}
            >
              {listingStatus && (
                <div className="mb-4 mt-[27px] flex h-[32px] w-fit items-center justify-center rounded-full bg-[#F8F7F5] px-[15px]">
                  <p className="text-[13px] font-semibold text-primary">{capitalize(listingStatus)}</p>
                </div>
              )}
              <h1 className="mb-1 text-center text-[26px] font-semibold leading-[1.30] text-primary">{item.title}</h1>
              <DotList
                items={[
                  // item.listingPrice ? `$${item.listingPrice}` : null,
                  item.bedrooms ? `${item.bedrooms} bed${item.bedrooms > 1 ? 's' : ''}` : null,
                  item.bathrooms ? `${item.bathrooms} bath${item.bathrooms > 1 ? 's' : ''}` : null,
                ]}
                textClassName="leading-[1] text-[15px]"
              />
              {item.listingPrice && (
                <div className="mt-[15px] flex h-[50px] w-fit min-w-[156px] items-center justify-center rounded-[16px] bg-[#F8F7F5] px-[18px]">
                  <p className="text-[22px] font-semibold tracking-[-0.44px] text-primary">
                    ${formatCurrency(item.listingPrice)}
                  </p>
                </div>
              )}
              <p className="mt-5 pb-10 text-center text-[15px] font-medium leading-[1.7] text-muted">
                {item.description}
              </p>
            </div>
          </div>
          <PublicUserProfileFooter onSendInquiry={onSendInquiry} username={username} />
        </DialogContent>
      </Dialog>
    )
  }

  return (
    <>
      <Dialog open={isOpen} onOpenChange={setOpen}>
        <DialogContent
          hideClose
          className="max-h-[calc(100vh-100px)] w-[calc(100vw-40px)] max-w-[1000px] rounded-[20px] px-5 py-6 sm:rounded-[40px] md:px-[43px] md:py-[54px]"
        >
          <div className="absolute right-[35px] top-[37px] z-10">
            <CloseButton onClick={() => setOpen(false)} />
          </div>
          <div className="flex h-full max-h-[calc(100vh-200px)] flex-row gap-10 overflow-hidden">
            <div className="flex max-h-[620px] min-h-[390px] flex-1 shrink-0 overflow-hidden rounded-[20px]">
              <ImageGrid images={item.images} imageOnClick={imageOnClick} />
            </div>
            <div className="flex-1 overflow-y-auto py-[18px] pr-2">
              {listingStatus && (
                <div className="mb-4 flex h-[40px] w-fit items-center justify-center rounded-full bg-[#F8F7F5] px-[25px]">
                  <p className="text-[15px] font-semibold text-primary">{capitalize(listingStatus)}</p>
                </div>
              )}
              <h1 className="mb-1 text-[36px] font-semibold leading-tight text-primary">{item.title}</h1>
              <DotList
                items={[
                  // item.listingPrice ? `$${item.listingPrice}` : null,
                  item.bedrooms ? `${item.bedrooms} bed${item.bedrooms > 1 ? 's' : ''}` : null,
                  item.bathrooms ? `${item.bathrooms} bath${item.bathrooms > 1 ? 's' : ''}` : null,
                ]}
                textClassName="leading-[1] text-[20px] tracking-[-0.4px]"
              />
              <p className="mt-4 text-[18px] font-normal leading-[1.7] text-muted">{item.description}</p>
              {item.listingPrice && (
                <div className="mt-8 flex h-[65px] w-fit min-w-[210px] items-center justify-center rounded-[16px] bg-[#F8F7F5] px-[18px]">
                  <p className="text-[28px] font-semibold tracking-[-0.56px] text-primary">
                    ${formatCurrency(item.listingPrice)}
                  </p>
                </div>
              )}
            </div>
          </div>
        </DialogContent>
      </Dialog>
      <ImageCarouselDialog
        isOpen={imageCarouselState.isOpen}
        setOpen={(open) => setImageCarouselState({ ...imageCarouselState, isOpen: open })}
        images={item.images}
        startIndex={imageCarouselState.startIndex}
      />
    </>
  )
}
